import React, { useState } from "react";
import TopMostPortal from "../../components/ui/utils/Portals";
import RestAPI from "../../utils/restAPI";
import { logError } from "../../shared/common/logger";
import { toastNotify } from "../../shared/client/FrontendUtils";
import { IOrganizationUser } from "../../shared/common/interfaces";

interface DeleteUserModalProps {
	user: IOrganizationUser;
	organizationId: string;
	onCancel: () => void;
	onSuccess: () => void;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({ user, organizationId, onCancel, onSuccess }) => {
	const [isDeleting, setIsDeleting] = useState(false);

	const handleDelete = async () => {
		if (!user || !organizationId) return;

		setIsDeleting(true);
		try {
			const response = await RestAPI.fetchAPI(`/rest/access/organization/${organizationId}/user/${user.lassoUserId}`, "DELETE");

			if (response?.success === true) {
				toastNotify("User successfully deleted", false);
				onSuccess();
			} else {
				logError("Error deleting user: API returned success !== true", response);
				toastNotify("Failed to delete user. Please try again.", true);
			}
		} catch (err) {
			logError("Error deleting user", err);
			toastNotify("Failed to delete user. Please try again.", true);
		} finally {
			setIsDeleting(false);
		}
	};

	const userName = user.firstName || user.lastName ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "this user";

	return (
		<TopMostPortal>
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
				<div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
					<div className="flex items-start gap-4 mb-6">
						<div className="w-12 h-12 p-3 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
							<img src="/assets/ui/trash-02.svg" width="24" height="24" alt="trash-icon" />
						</div>
						<div className="flex-1">
							<div className="text-gray-900 text-lg font-semibold mb-1">Delete User</div>
							<div className="text-slate-600 text-sm">
								Are you sure you want to delete {userName} ({user.email})? This action cannot be undone.
							</div>
						</div>
					</div>
					<div className="flex justify-end gap-3">
						<button
							onClick={onCancel}
							disabled={isDeleting}
							className="px-4 py-2.5 bg-white rounded-lg border border-gray-200 text-slate-700 text-base font-semibold hover:bg-gray-50 disabled:opacity-50"
						>
							Cancel
						</button>
						<button
							onClick={handleDelete}
							disabled={isDeleting}
							className="px-4 py-2.5 bg-red-600 rounded-lg shadow border border-red-600 text-white text-base font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isDeleting ? "Deleting..." : "Delete"}
						</button>
					</div>
				</div>
			</div>
		</TopMostPortal>
	);
};

export default DeleteUserModal;
